import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Zap, MapPin, Search, Briefcase, Plus, TrendingUp, Clock, CheckCircle2 } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { JobCard } from "@/components/JobCard";
import { StatusBadge } from "@/components/StatusBadge";
import { EmptyState, Loading } from "@/components/EmptyState";
import { Button } from "@/components/ui/button";

export default function Dashboard() {
  const { user, mode } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [jobs, setJobs] = useState([]);
  const [myApps, setMyApps] = useState([]);
  const [myJobs, setMyJobs] = useState([]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        if (mode === "worker") {
          const [j, a] = await Promise.all([api.get("/jobs?limit=8&sort=nearest"), api.get("/applications/mine")]);
          setJobs(j.data); setMyApps(a.data);
        } else {
          const m = await api.get("/jobs/mine/employer");
          setMyJobs(m.data);
        }
      } finally { setLoading(false); }
    })();
  }, [mode]);

  if (loading) return <Loading />;

  return (
    <div className="space-y-8" data-testid="dashboard">
      <div>
        <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900">Selamat datang, {user.name} 👋</h1>
        <p className="text-slate-500 mt-1 flex items-center gap-1.5">
          <MapPin size={16} className="text-blue-600" /> {user.city || "Lokasi belum diatur"}
          <span className="mx-1">·</span>
          Mode {mode === "worker" ? "Mencari Pekerjaan" : "Mencari Tenaga"}
        </p>
      </div>

      {mode === "worker" ? (
        <>
          {/* Stats */}
          <div className="grid grid-cols-3 gap-3">
            <StatCard icon={Briefcase} label="Lamaran" value={myApps.length} />
            <StatCard icon={CheckCircle2} label="Selesai" value={user.completed_jobs} />
            <StatCard icon={TrendingUp} label="Rating" value={user.rating_count ? user.rating_average.toFixed(1) : "—"} />
          </div>

          <section>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold flex items-center gap-2"><Zap className="text-amber-500 fill-amber-400" size={22} /> Quick Jobs</h2>
              <Button data-testid="dash-see-all" variant="ghost" onClick={() => navigate("/cari")} className="text-blue-600 font-semibold">Lihat semua</Button>
            </div>
            {jobs.length === 0 ? (
              <EmptyState icon={Search} title="Belum ada pekerjaan" description="Coba lagi nanti atau ubah lokasi profilmu." />
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {jobs.map((j, i) => <JobCard key={j.id} job={j} index={i} />)}
              </div>
            )}
          </section>

          <section>
            <h2 className="text-xl font-bold mb-4">Lamaran saya</h2>
            {myApps.length === 0 ? (
              <EmptyState icon={Briefcase} title="Belum ada lamaran" description="Lamar pekerjaan pertamamu sekarang." actionLabel="Cari Pekerjaan" onAction={() => navigate("/cari")} />
            ) : (
              <div className="space-y-3">
                {myApps.slice(0, 5).map((a) => (
                  <div key={a.id} data-testid={`app-row-${a.id}`} onClick={() => navigate(`/pekerjaan/${a.job_id}`)}
                    className="bg-white rounded-xl border border-slate-200 p-4 flex items-center justify-between hover:border-slate-300 cursor-pointer transition-colors">
                    <div className="min-w-0">
                      <p className="font-semibold text-slate-900 truncate">{a.job?.title || "Pekerjaan"}</p>
                      <p className="text-sm text-slate-500 flex items-center gap-1"><MapPin size={13} /> {a.job?.city}</p>
                    </div>
                    <StatusBadge status={a.status} kind="app" />
                  </div>
                ))}
              </div>
            )}
          </section>
        </>
      ) : (
        <>
          <div className="grid grid-cols-3 gap-3">
            <StatCard icon={Briefcase} label="Total" value={myJobs.length} />
            <StatCard icon={Clock} label="Aktif" value={myJobs.filter((j) => ["OPEN", "ASSIGNED", "IN_PROGRESS"].includes(j.status)).length} />
            <StatCard icon={CheckCircle2} label="Selesai" value={myJobs.filter((j) => j.status === "COMPLETED").length} />
          </div>
          <div className="bg-blue-600 rounded-2xl p-6 text-white flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold">Butuh bantuan?</h2>
              <p className="text-blue-100 text-sm mt-1">Posting pekerjaan dan temukan tenaga terbaik.</p>
            </div>
            <Button data-testid="dash-create-btn" onClick={() => navigate("/buat")} className="bg-white text-blue-700 hover:bg-blue-50 rounded-lg font-bold shrink-0">
              <Plus size={18} className="mr-1" /> Buat Pekerjaan
            </Button>
          </div>
          <section>
            <h2 className="text-xl font-bold mb-4">Pekerjaan terbaru</h2>
            {myJobs.length === 0 ? (
              <EmptyState icon={Briefcase} title="Belum ada pekerjaan" description="Buat pekerjaan pertamamu." actionLabel="+ Buat Pekerjaan" onAction={() => navigate("/buat")} />
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {myJobs.slice(0, 6).map((j, i) => (
                  <JobCard key={j.id} job={j} index={i} showStatus
                    footer={j.pending_count > 0 && <div className="text-sm font-semibold text-amber-600 pt-1">{j.pending_count} pelamar baru</div>} />
                ))}
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
}

function StatCard({ icon: Icon, label, value }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-4">
      <Icon size={18} className="text-blue-600 mb-2" />
      <p className="text-2xl font-extrabold text-slate-900">{value}</p>
      <p className="text-xs text-slate-500">{label}</p>
    </div>
  );
}
